import type { ErrorLike } from '@apollo/client';
import { useCallback } from 'react';

import {
  type AtlasGqlTableView,
  AtlasGqlViewType,
  useGetBladeDefectPatternsLazyQuery,
  useGetBladeDefectPatternsQuery,
} from 'types/atlas-graphql';

type DefectPatternViewsResult = {
  views: AtlasGqlTableView[];
  loading: boolean;
  error?: ErrorLike;
};

export type DefectPatternViewCustomFields = {
  bladeMake?: string;
  bladeModel?: string;
  [key: string]: unknown;
};

export const isDefectPatternViewType = (type?: AtlasGqlViewType | null): boolean =>
  type === AtlasGqlViewType.BladeDefectPattern;

// A Turbine-matched Defect Pattern has no Blade make/model, so it names a representative
// Blade to scale diagrams against. Half a pair reads as absent, never mixed with filters.
export const getRepresentativeBlade = (
  view?: AtlasGqlTableView | null
): { make: string; model: string } | undefined => {
  const { bladeMake, bladeModel } =
    (view?.customFields as DefectPatternViewCustomFields | null) ?? {};
  return bladeMake && bladeModel ? { make: bladeMake, model: bladeModel } : undefined;
};

export const useDefectPatternViews = (): DefectPatternViewsResult => {
  const { data, loading, error } = useGetBladeDefectPatternsQuery();
  return { views: data?.bladeDefectPatternTableViews ?? [], loading, error };
};

export const useDefectPatternViewsLazy = (): (() => Promise<AtlasGqlTableView[]>) => {
  const [loadDefectPatterns] = useGetBladeDefectPatternsLazyQuery();

  return useCallback(async () => {
    const { data } = await loadDefectPatterns();
    return data?.bladeDefectPatternTableViews ?? [];
  }, [loadDefectPatterns]);
};
